import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import GoogleSignInButton from "../components/GoogleSignInButton";

const COAT = "https://customer-assets.emergentagent.com/job_66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/artifacts/xbhowqoo_ephigie-hayti.jpg";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      const next = new URLSearchParams(loc.search).get("next");
      if (next) nav(next);
      else if (u.role === "admin") nav("/admin");
      else if (u.role === "trainer") nav("/trainer");
      else nav("/student");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="login-page">
      <Header />
      <div className="max-w-md mx-auto px-5 py-20">
        <div className="text-center mb-10">
          <img src={COAT} alt="" className="h-16 w-16 mx-auto object-contain mix-blend-screen mb-4" />
          <h1 className="font-serif text-4xl">{t("auth.login_welcome", t("auth.login"))}</h1>
          <p className="text-neutral-400 mt-2 text-sm">{t("auth.loginSub")}</p>
        </div>
        <form onSubmit={onSubmit} className="card-imperial p-8 space-y-5" data-testid="login-form">
          <GoogleSignInButton
            next={new URLSearchParams(loc.search).get("next") || "/student"}
            testId="login-google-btn"
          />
          <div className="flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-[0.2em]">
            <div className="flex-1 h-px bg-[#1a1a1a]" />
            <span>{t("auth.orDivider", "or")}</span>
            <div className="flex-1 h-px bg-[#1a1a1a]" />
          </div>
          <Field label={t("auth.email")} type="email" value={email} onChange={setEmail} testId="login-email" required />
          <Field label={t("auth.password")} type="password" value={password} onChange={setPassword} testId="login-password" required />
          {err && <div data-testid="login-error" className="text-sm text-[#e6192b]">{err}</div>}
          <button type="submit" disabled={busy} data-testid="login-submit" className="btn-imperial w-full">
            {busy ? t("common.loading") : t("auth.submitLogin")}
          </button>
          <div className="text-sm text-neutral-500 text-center pt-2">
            {t("auth.noAccount")}{" "}
            <Link to="/register" data-testid="login-to-register" className="text-[#d4af37] hover:underline">
              {t("auth.submitRegister")}
            </Link>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId, required }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm transition-colors"
      />
    </label>
  );
}
